// WebSocket bridge for MCP server communication

import { zoneAPI } from './ZoneAPI'
import { ALL_ZONE_TYPES, PIN_TYPES } from '@/constants/zoneTypes'
import { debug } from '@/utils/debug'
import type { ZoneType, PinType, ZoneState } from '@/types/zone'
import { uugService } from '@/geodata/UUGService'
import { uldkService } from '@/geodata/ULDKService'
import { setGeoAnchor, getGeoAnchor } from '@/geodata/geoAnchorState'
import { importParcelAsBoundary, getBoundaryBounds } from '@/geodata/boundaryImport'
import type { GeoAnchor } from '@/geodata/types'
import { editorAPI } from '@/api/EditorAPI'
import type { Layer } from '@/types'

const WS_PORT = 3333
const WS_URL = `ws://localhost:${WS_PORT}`
const RECONNECT_DELAY = 3000

export type ConnectionStatus = 'connected' | 'connecting' | 'disconnected'

let ws: WebSocket | null = null
let reconnectTimeout: ReturnType<typeof setTimeout> | null = null
let isConnecting = false
let shouldReconnect = true
let statusChangeCallback: ((status: ConnectionStatus) => void) | null = null

/**
 * Register a callback for connection status changes
 */
export function onConnectionStatusChange(cb: ((status: ConnectionStatus) => void) | null): void {
  statusChangeCallback = cb
}

/**
 * Get current connection status
 */
export function getConnectionStatus(): ConnectionStatus {
  if (ws && ws.readyState === WebSocket.OPEN) return 'connected'
  if (isConnecting) return 'connecting'
  return 'disconnected'
}

// Callback for when boundary is imported via MCP
let boundaryImportedCallback: ((parcel: import('@/geodata/types').ParcelResult, schemeOutline: {
  x: number; y: number; points: Array<{ x: number; y: number }>
}) => void) | null = null

/**
 * Register a callback for when a parcel boundary is imported via MCP
 */
export function onMcpBoundaryImported(cb: typeof boundaryImportedCallback): void {
  boundaryImportedCallback = cb
}

interface WSRequest {
  type: 'request'
  id: string
  method: string
  params: Record<string, unknown>
}

interface WSResponse {
  type: 'response'
  id: string
  result?: unknown
  error?: string
}

/**
 * Handle incoming request from MCP server
 */
async function handleRequest(request: WSRequest): Promise<unknown> {
  const { method, params } = request

  switch (method) {
    // ── Zone tools ──

    case 'add_zone': {
      const { zoneType, x, y, width, height, elevation, label, notes, points } = params as {
        zoneType: ZoneType
        x: number
        y: number
        width: number
        height: number
        elevation?: number
        label?: string
        notes?: string
        points?: Array<{ x: number; y: number }>
      }
      const id = zoneAPI.addZone(zoneType, x, y, width, height, { elevation, label, notes, points })
      return { id, success: true }
    }

    case 'add_zones': {
      const { zones } = params as {
        zones: Array<{
          zoneType: ZoneType; x: number; y: number; width: number; height: number
          elevation?: number; label?: string; notes?: string; points?: Array<{ x: number; y: number }>
        }>
      }
      const results = zones.map(z => {
        const id = zoneAPI.addZone(z.zoneType, z.x, z.y, z.width, z.height, {
          elevation: z.elevation, label: z.label, notes: z.notes, points: z.points,
        })
        return { id, success: true }
      })
      return { results, count: results.length }
    }

    case 'update_zone': {
      const { id, ...updates } = params as { id: string } & Record<string, unknown>
      zoneAPI.updateZone(id, updates)
      return { success: true }
    }

    case 'delete_zone': {
      const { id } = params as { id: string }
      const deleted = zoneAPI.deleteZone(id)
      return { success: deleted }
    }

    case 'get_zones': {
      return zoneAPI.getZones()
    }

    case 'get_objects_in_zone': {
      const { zoneId } = params as { zoneId: string }
      return editorAPI.getObjectsInZone(zoneId)
    }

    case 'get_zone_analysis': {
      const { zoneId } = params as { zoneId: string }
      return editorAPI.getZoneAnalysis(zoneId)
    }

    case 'add_pin': {
      const { pinType, x, y, label, notes } = params as {
        pinType: PinType
        x: number
        y: number
        label?: string
        notes?: string
      }
      const id = zoneAPI.addPin(pinType, x, y, { label, notes })
      return { id, success: true }
    }

    case 'add_pins': {
      const { pins } = params as {
        pins: Array<{ pinType: PinType; x: number; y: number; label?: string; notes?: string }>
      }
      const results = pins.map(p => {
        const id = zoneAPI.addPin(p.pinType, p.x, p.y, { label: p.label, notes: p.notes })
        return { id, success: true }
      })
      return { results, count: results.length }
    }

    case 'delete_pin': {
      const { id } = params as { id: string }
      const deleted = zoneAPI.deletePin(id)
      return { success: deleted }
    }

    case 'get_pins': {
      return zoneAPI.getPins()
    }

    case 'clear_all': {
      zoneAPI.clearAll()
      return { success: true }
    }

    case 'get_zone_types': {
      return {
        zoneTypes: ALL_ZONE_TYPES,
        pinTypes: PIN_TYPES,
      }
    }

    case 'export_state': {
      return zoneAPI.exportState()
    }

    case 'import_state': {
      const { state } = params as { state: ZoneState }
      zoneAPI.importState(state)
      return { success: true }
    }

    // ── Geodata tools ──

    case 'geocode_address': {
      const { address } = params as { address: string }
      const result = await uugService.geocode(address)
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return {
        address: result.value.address,
        lat: result.value.wgs84.lat,
        lon: result.value.wgs84.lon,
        confidence: result.value.confidence,
      }
    }

    case 'get_parcel_by_coordinates': {
      const { lat, lon } = params as { lat: number; lon: number }
      const result = await uldkService.getParcelByXY({ lat, lon })
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return formatParcelResult(result.value)
    }

    case 'get_parcel_by_id': {
      const { parcelId } = params as { parcelId: string }
      const result = await uldkService.getParcelById(parcelId)
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return formatParcelResult(result.value)
    }

    case 'get_parcel_by_address': {
      const { address } = params as { address: string }
      const result = await uldkService.getParcelByAddress(address)
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return formatParcelResult(result.value)
    }

    case 'set_geo_anchor': {
      const { lat, lon, editorX, editorY, dotsPerMeter } = params as {
        lat: number
        lon: number
        editorX?: number
        editorY?: number
        dotsPerMeter?: number
      }
      const anchor: GeoAnchor = {
        origin: { lat, lon },
        editorOrigin: { x: editorX ?? 0, y: editorY ?? 0 },
        dotsPerMeter: dotsPerMeter ?? 50,
      }
      setGeoAnchor(anchor)
      return { success: true, anchor }
    }

    case 'get_geo_anchor': {
      const anchor = getGeoAnchor()
      if (!anchor) {
        return { set: false, anchor: null }
      }
      return {
        set: true,
        anchor: {
          lat: anchor.origin.lat,
          lon: anchor.origin.lon,
          editorX: anchor.editorOrigin.x,
          editorY: anchor.editorOrigin.y,
          dotsPerMeter: anchor.dotsPerMeter,
        },
      }
    }

    case 'import_parcel_boundary': {
      const { parcelId } = params as { parcelId: string }
      // Fetch the parcel first
      const parcelResult = await uldkService.getParcelById(parcelId)
      if (!parcelResult.ok) {
        throw new Error(parcelResult.error.message)
      }
      // Import as boundary
      const { anchor, schemeOutline } = importParcelAsBoundary(parcelResult.value)
      const bounds = getBoundaryBounds(schemeOutline.points)
      // Notify UI to render the boundary and update map tab
      boundaryImportedCallback?.(parcelResult.value, schemeOutline)
      return {
        success: true,
        parcelId: parcelResult.value.id,
        area: parcelResult.value.area,
        region: parcelResult.value.region,
        anchor: {
          lat: anchor.origin.lat,
          lon: anchor.origin.lon,
          editorX: anchor.editorOrigin.x,
          editorY: anchor.editorOrigin.y,
          dotsPerMeter: anchor.dotsPerMeter,
        },
        schemeOutline,
        bounds,
      }
    }

    // ── EditorAPI tools ──

    case 'get_garden_info':
      return editorAPI.getGardenInfo()

    case 'set_garden_rotation': {
      const { degrees } = params as { degrees: number }
      return editorAPI.setGardenRotation(degrees)
    }

    case 'set_garden_description': {
      const { description } = params as { description: string }
      return editorAPI.setGardenDescription(description)
    }

    case 'get_objects': {
      const { layer } = params as { layer?: Layer }
      return editorAPI.getObjects(layer ? { layer } : undefined)
    }

    case 'add_object':
      return editorAPI.addObjectResolved(params as Parameters<typeof editorAPI.addObjectResolved>[0])

    case 'add_objects': {
      const { objects } = params as {
        objects: Array<Parameters<typeof editorAPI.addObjectResolved>[0]>
      }
      return editorAPI.addObjectsBatchResolved(objects)
    }

    case 'update_object':
      return editorAPI.updateObject(params as { id: string; [key: string]: unknown })

    case 'update_objects': {
      const { objects } = params as {
        objects: Array<{ id: string; [key: string]: unknown }>
      }
      const results = objects.map(obj => {
        try {
          return editorAPI.updateObject(obj)
        } catch (e) {
          return { success: false, error: e instanceof Error ? e.message : String(e) }
        }
      })
      return { results, count: results.filter(r => r.success).length }
    }

    case 'delete_object': {
      const { id } = params as { id: string }
      return editorAPI.deleteObject(id)
    }

    case 'delete_objects': {
      const { ids } = params as { ids: string[] }
      const results = ids.map(id => {
        try {
          return { id, ...editorAPI.deleteObject(id) }
        } catch (e) {
          return { id, success: false, error: e instanceof Error ? e.message : String(e) }
        }
      })
      return { results, count: results.filter(r => r.success).length }
    }

    case 'search_products':
      return editorAPI.searchProducts(params as Parameters<typeof editorAPI.searchProducts>[0])

    case 'get_categories': {
      const { layer } = params as { layer: Layer }
      return editorAPI.getCategories(layer)
    }

    case 'get_product_details': {
      const { productId } = params as { productId: string }
      return editorAPI.getProductDetails(productId)
    }

    case 'get_plant_info': {
      const { productId } = params as { productId: string }
      return editorAPI.getPlantInfo(productId)
    }

    case 'capture_preview': {
      const { maxSize } = params as { maxSize?: number }
      return editorAPI.capturePreview(maxSize)
    }

    case 'set_view_mode': {
      const { mode } = params as { mode: '2d' | '3d' }
      return editorAPI.setViewMode(mode)
    }

    case 'get_view_mode':
      return editorAPI.getViewMode()

    case 'focus_on': {
      const { x, y, width, height } = params as { x: number; y: number; width?: number; height?: number }
      return editorAPI.focusOn(x, y, width, height)
    }

    case 'set_layer_visible': {
      const { layer, visible } = params as { layer: string; visible: boolean }
      return editorAPI.setLayerVisible(layer, visible)
    }

    case 'get_layer_visibility':
      return editorAPI.getLayerVisibility()

    case 'save':
      return editorAPI.save()

    case 'undo':
      return editorAPI.undo()

    case 'redo':
      return editorAPI.redo()

    case 'upload_reference_photo': {
      const { dataUri, label } = params as { dataUri: string; label: string }
      return editorAPI.uploadReferencePhoto(dataUri, label)
    }

    case 'get_reference_photos':
      return editorAPI.getReferencePhotos()

    case 'get_coordinate_system':
      return editorAPI.getCoordinateSystem()

    case 'get_full_state': {
      const anchor = getGeoAnchor()
      const [gardenInfo, zones, pins, objects, layerVisibility, viewMode, coordinateSystem] = await Promise.all([
        Promise.resolve(editorAPI.getGardenInfo()),
        Promise.resolve(zoneAPI.getZones()),
        Promise.resolve(zoneAPI.getPins()),
        Promise.resolve(editorAPI.getObjects()),
        Promise.resolve(editorAPI.getLayerVisibility()),
        Promise.resolve(editorAPI.getViewMode()),
        Promise.resolve(editorAPI.getCoordinateSystem()),
      ])

      // Compute summary for quick agent orientation
      const zonesByType: Record<string, number> = {}
      for (const z of zones as Array<{ zoneType: string }>) {
        zonesByType[z.zoneType] = (zonesByType[z.zoneType] || 0) + 1
      }
      const pinsByType: Record<string, number> = {}
      for (const p of pins as Array<{ pinType: string }>) {
        pinsByType[p.pinType] = (pinsByType[p.pinType] || 0) + 1
      }
      const objectsByLayer: Record<string, number> = {}
      for (const obj of objects as Array<{ type: string }>) {
        const layer = obj.type === 'plant' ? 'plants'
          : obj.type === 'building' ? 'architecture'
          : 'ground'
        objectsByLayer[layer] = (objectsByLayer[layer] || 0) + 1
      }

      const info = gardenInfo as { name?: string; description?: string; rotation?: number; boundary?: unknown }
      const summary = {
        gardenName: info.name || null,
        description: info.description || null,
        rotation: info.rotation ?? 0,
        hasBoundary: !!info.boundary,
        hasGeoAnchor: anchor !== null,
        zoneCount: (zones as unknown[]).length,
        zonesByType,
        pinCount: (pins as unknown[]).length,
        pinsByType,
        objectCount: (objects as unknown[]).length,
        objectsByLayer,
      }

      return {
        summary,
        gardenInfo,
        zones,
        pins,
        objects,
        layerVisibility,
        viewMode,
        coordinateSystem,
        geoAnchor: anchor ? {
          set: true,
          lat: anchor.origin.lat,
          lon: anchor.origin.lon,
          editorX: anchor.editorOrigin.x,
          editorY: anchor.editorOrigin.y,
          dotsPerMeter: anchor.dotsPerMeter,
        } : { set: false },
      }
    }

    default:
      throw new Error(`Unknown method: ${method}`)
  }
}

/**
 * Format ParcelResult for MCP response
 */
function formatParcelResult(parcel: import('@/geodata/types').ParcelResult) {
  return {
    id: parcel.id,
    region: parcel.region,
    area: parcel.area,
    address: parcel.address,
    vertexCount: parcel.boundary.length,
    boundary: parcel.boundary.map(c => ({ lat: c.lat, lon: c.lon })),
  }
}

/**
 * Connect to the MCP WebSocket server
 */
function connect(): void {
  if (isConnecting || (ws && ws.readyState === WebSocket.OPEN)) {
    return
  }

  isConnecting = true
  shouldReconnect = true
  statusChangeCallback?.('connecting')

  try {
    ws = new WebSocket(WS_URL)

    ws.onopen = () => {
      isConnecting = false
      debug.success('WebSocket', `Connected to MCP server at ${WS_URL}`)
      console.log('%c[Garden Zones]%c Connected to MCP server', 'color: #4ecca3; font-weight: bold', 'color: inherit')
      statusChangeCallback?.('connected')

      // Clear any pending reconnect
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout)
        reconnectTimeout = null
      }
    }

    ws.onmessage = async (event) => {
      try {
        const message = JSON.parse(event.data) as WSRequest

        if (message.type === 'request') {
          debug.log('WebSocket', `Received: ${message.method}`, message.params)

          let result: unknown
          let error: string | undefined

          try {
            result = await handleRequest(message)
          } catch (e) {
            error = e instanceof Error ? e.message : String(e)
            debug.error('WebSocket', `Error handling ${message.method}:`, e)
          }

          // Send response back
          const response: WSResponse = {
            type: 'response',
            id: message.id,
            result,
            error,
          }

          ws?.send(JSON.stringify(response))
          debug.log('WebSocket', `Sent response for ${message.method}`, result)
        }
      } catch (e) {
        debug.error('WebSocket', 'Error parsing message:', e)
      }
    }

    ws.onclose = () => {
      isConnecting = false
      debug.log('WebSocket', 'Disconnected from MCP server')
      statusChangeCallback?.('disconnected')

      // Attempt to reconnect only if not explicitly disconnected
      if (shouldReconnect && !reconnectTimeout) {
        reconnectTimeout = setTimeout(() => {
          reconnectTimeout = null
          connect()
        }, RECONNECT_DELAY)
      }
    }

    ws.onerror = (error) => {
      isConnecting = false
      // Only log error if we were previously connected
      if (ws?.readyState === WebSocket.OPEN) {
        debug.error('WebSocket', 'Connection error:', error)
      }
    }
  } catch (e) {
    isConnecting = false
    debug.error('WebSocket', 'Failed to create connection:', e)
  }
}

/**
 * Disconnect from the MCP server
 */
function disconnect(): void {
  shouldReconnect = false

  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout)
    reconnectTimeout = null
  }

  if (ws) {
    ws.close()
    ws = null
  }

  isConnecting = false
  statusChangeCallback?.('disconnected')
}

/**
 * Check if connected to MCP server
 */
function isConnected(): boolean {
  return ws !== null && ws.readyState === WebSocket.OPEN
}

/**
 * Initialize WebSocket bridge (call this in edit mode)
 */
export function initWebSocketBridge(): void {
  debug.log('WebSocket', 'Initializing bridge...')
  connect()

  // Handle page unload
  window.addEventListener('beforeunload', disconnect)
}

/**
 * Dispose WebSocket bridge
 */
export function disposeWebSocketBridge(): void {
  disconnect()
}

// Export for debugging
export const wsBridge = {
  connect,
  disconnect,
  isConnected,
}
